import { ApiQueryParams } from '@frontend/api-utils';
import { ObjectSelectInput, ObjectSelectProps } from '@frontend/components/basic-forms';
import { CommonMessage } from '@frontend/lang';
import { UserClient } from '@frontend/user/api';
import { IUser, UserListResponse, UserQueryParams } from '@frontend/user/types';
import { User } from '@frontend/user/utils';
import React, { useState } from 'react';

import { SelectedUserDetails } from './selected-user-details';

export { SelectedUserDetails };
export type { SelectedUserDetailsProps } from './selected-user-details';

const ID = 'user-select';

export interface UserSelectV2Props extends ObjectSelectProps<IUser, UserQueryParams> {
    fetchOverwrite?: (queryParams?: ApiQueryParams<UserQueryParams>) => Promise<UserListResponse>;
    showSelectedDetails?: boolean;
}

export const UserSelectV2 = (props: UserSelectV2Props) => {
    const { fetchOverwrite, customLabel, showSelectedDetails, ...selectProps } = props;
    const [v, cV] = useState<IUser | null>(null);
    const itemLabel = (user: IUser) => (customLabel ? customLabel(user) : new User(user).getDisplayName());

    React.useEffect(() => {
        if (selectProps.value == null) {
            cV(null);
        }
    }, [selectProps.value]);

    return (
        <div className='flex w-full flex-col gap-1'>
            <ObjectSelectInput<IUser, UserQueryParams>
                {...selectProps}
                id={`${ID}${props.id ? '-' + props.id : ''}`}
                label={props.label || CommonMessage.OBJECTS.USER.DEFAULT}
                helpMessage={props.helpMessage}
                className={props.className}
                onChange={(selected) => {
                    const nextId = selected?.id ?? null;
                    if (nextId !== (selectProps.value ?? null)) {
                        selectProps.onChange?.(nextId);
                    }
                    selectProps.onChangeObject?.(selected);
                    cV(selected);
                }}
                disabled={props.disabled}
                required={props.required}
                queryParams={props.queryParams}
                value={v}
                isClearable={props.isClearable}
                idValue={props.value !== undefined ? props.value : v?.id}
                fetch={(arg) => (fetchOverwrite ? fetchOverwrite(arg) : UserClient.fetchUsers({ ...arg, sort_field: 'first_name', sort_direction: 'asc' }))}
                singleFetch={(id) => UserClient.resolveUsers([id]).then((res) => res.results[0])}
                itemLabel={itemLabel}
                useQueryParam={
                    selectProps.allowURLChange
                        ? {
                              param: 'user_id',
                              fetch: (id) => UserClient.resolveUsers([id]).then((res) => res.results[0])
                          }
                        : undefined
                }
                subset={props.subset}
            />
            {showSelectedDetails ? (
                <SelectedUserDetails
                    user={v}
                    userId={selectProps.value ?? v?.id ?? null}
                />
            ) : null}
        </div>
    );
};
